import { observer } from 'mobx-react-lite'
import { useEffect, useState } from 'react'

import { CrmActivitiesTab, canReadActivities } from '@/Containers/CrmSection/Components/Activities'
import { CrmCommentsAside } from '@/Containers/CrmSection/Components/Comments'
import { CrmContactsTab } from '@/Containers/CrmSection/Components/Contacts'
import { CrmContractsTab } from '@/Containers/CrmSection/Components/Contracts'
import { CrmDocumentsTab } from '@/Containers/CrmSection/Components/Documents'
import { CrmHistoryTab } from '@/Containers/CrmSection/Components/History'
import { CrmLettersTab, canReadLetters } from '@/Containers/CrmSection/Components/Letters'
import { VerificationRequirementsAlert } from '@/Containers/CrmSection/Components/VerificationButtons'
import { ClientApplicationsTab, canReadApplications } from '@/Containers/TransportationSection/ApplicationContainer/Hooks'
import { ClientRequestsTab, canReadRequests } from '@/Containers/TransportationSection/RequestContainer/Hooks'
import { ClientTendersTab, canReadTenders } from '@/Containers/TransportationSection/TenderContainer/Hooks'
import { Form, FormField } from '@/Core/UI/Form'
import { CostCurrencyField } from '@/Ship/Components/CostCurrencyField'
import {
    EntityBody,
    EntityLayout,
    EntityPanel,
    EntityTabs,
    FieldCell,
    FieldGrid,
    FieldSection,
} from '@/Ship/Components/EntityCard'
import { Hint } from '@/Ship/Components/Hint'

import { ClientCardHeader } from '../Components/ClientCardHeader'
import { ClientStatusCell } from '../Components/ClientStatusCell'
import { CLIENT_COST_FIELDS, CLIENT_FIELD_LABELS } from '../Contracts/ClientFieldLabels'
import { CLIENT_HISTORY_EVENT_MAP } from '../Contracts/ClientHistoryEvent'
import {
    CLIENT_COMMENT_REST_URL,
    CLIENT_CONTACT_RESOURCE,
    CLIENT_CONTACT_REST_URL,
    CLIENT_CONTRACT_RESOURCE,
    CLIENT_CONTRACT_REST_URL,
    CLIENT_DOCUMENT_RESOURCE,
    CLIENT_DOCUMENT_REST_URL,
    CLIENT_REST_URL,
} from '../Contracts/IClient'
import { ApplyClientCreateDefaultsTask } from '../Tasks/ApplyClientCreateDefaultsTask'

import type { ClientCrudContextLike, ClientFormContextLike } from '../Contracts/IClient'
import type { ReactNode } from 'react'

interface Props {
    crudContext: ClientCrudContextLike | undefined
    formContext: ClientFormContextLike | undefined
}

/** Місце статусу в блоці «Загальне»: його малює не `FormField`, а `ClientStatusCell`. */
const STATUS = 'status'

/** Біля цього поля — підказка `crm.client.responsibles` (`SPEC-009` §13). */
const RESPONSIBLES = 'responsibles'

/**
 * Блоки «Загального» (`SPEC-009` §8.3), кожен відповідає на одне питання:
 *
 *  - **Загальне** — хто це й чий: назва першою, відповідальні, статус (після
 *    створення), контакти, теги, коментар;
 *  - **Реквізити** — для документів: коди, податки, банк, директор, адреса.
 *    У легасі ЄДРПОУ стояв серед загальних полів — тепер поруч з ІПН;
 *  - **Верифікація** — склад і порядок полів легасі; суми — одним полем із
 *    валютою (`CostCurrencyField`), «Ким верифіковано» й далі — лише перегляд.
 *    «Коментар до верифікації» видно в будь-якому статусі, як у легасі
 *    (`SPEC-009` Q9.3, рішення 2026-10-14);
 *  - **Службова інформація** — хто й коли створив і змінив, останнім.
 */
const SECTIONS = {
    general: ['title', 'fullName', 'responsibles', STATUS, 'emails', 'phones', 'tags', 'comment'],
    requisites: ['edrpou', 'ipn', 'formTaxation', 'numberVAT', 'iban', 'bank', 'mfo', 'manager', 'address'],
    verification: [
        'forwarder',
        'contactPerson',
        'positionContactPerson',
        'contactPersonPhone',
        'contactPersonEmail',
        'typeCargo',
        'approximateCostCargo',
        'routeFirstShipment',
        'costFirstShipmentClient',
        'costFirstShipmentCarrier',
        'clientPaymentTerm',
        'verifiedBy',
        'verifiedAt',
        'commentVerification',
    ],
    service: ['createdAt', 'createdBy', 'updatedAt', 'updatedBy'],
} as const

/** На всю ширину: назва й довгі тексти (`SPEC-009` §8.3). */
const WIDE = new Set<string>(['title', 'comment', 'address', 'commentVerification'])

/** Ставить сервер — у формі нового клієнта їм нема чого показати. */
const SERVER_FIELDS = new Set<string>([STATUS, 'verifiedBy', 'verifiedAt', 'commentVerification', ...SECTIONS.service])

/** Сума → валюта: пару малює один `CostCurrencyField`. */
const COST_BY_AMOUNT = new Map<string, string>(CLIENT_COST_FIELDS.map(({ amount, currency }) => [amount, currency]))

/**
 * Розставлене по блоках, а також те, що в картці не показується: «Ід» —
 * колонка й фільтр реєстру, а не поле картки.
 */
const PLACED = new Set<string>([
    ...Object.values(SECTIONS).flat(),
    ...CLIENT_COST_FIELDS.map(({ currency }) => currency),
    'id',
])

/**
 * Значення за замовчуванням форми створення — один раз на відкриту форму:
 * відповідальний — той, хто створює, валюта сум — EUR (КЛ-02, КЛ-08).
 *
 * Окремий компонент без рендера: шаблон значень форми не читає, а ефект
 * тут — лише запуск `ApplyClientCreateDefaultsTask`.
 */
const ClientCreateDefaults = ({ formContext }: { formContext: ClientFormContextLike }) => {
    useEffect(() => {
        void ApplyClientCreateDefaultsTask(formContext)
    }, [formContext])

    return null
}

/**
 * Картка клієнта на спільних правилах карток (`EntityLayout`, `SPEC-008` §7.1,
 * `SPEC-009` §8.2): шапка, вкладки, блоки полів двома стовпцями.
 *
 * Праворуч — компактні «Контакти» (списком карток) і стрічка коментарів клієнта
 * (`CrmCommentsAside`); ліворуч після «Загального» — компактні «Справи» (2026-10-14).
 * У формі створення компактних блоків немає, а на місці стрічки — пояснення,
 * коли вона з'явиться.
 *
 * Вкладки — лише в збереженого клієнта: «Загальне», «Контакти», «Договори»,
 * «Друковані форми» (лише верифікованому, КЛ-26), «Листи» (S8b, вміст —
 * `LetterContainer/Hooks`), «Справи» (S6a, вміст — `ActivityContainer/Hooks`),
 * «Історія змін» — остання.
 *
 * ⚠️ Значень полів, які редагують, шаблон не читає — лише `uuid` і
 * обчислюваний `isVerified` (`SPEC-002` §4.2): інакше ядровий `FormField`
 * перемонтовував би всі поля на кожну літеру. Живі значення показують
 * окремі observer-компоненти (`ClientCardHeader`, `ClientStatusCell`,
 * `CostCurrencyField`).
 */
export const ClientFormTemplate = observer(({ crudContext, formContext }: Props) => {
    // Лічильники відкриттів вкладок-списків: antd тримає вкладку змонтованою,
    // і без перемонтування вона не побачила б щойно збережених змін. Стрічку
    // коментарів не перемонтовуємо — щоб не губити чернетку
    const [historyEpoch, setHistoryEpoch] = useState(0)
    const [contactsEpoch, setContactsEpoch] = useState(0)
    const [contractsEpoch, setContractsEpoch] = useState(0)
    const [documentsEpoch, setDocumentsEpoch] = useState(0)
    const [activitiesEpoch, setActivitiesEpoch] = useState(0)
    const [requestsEpoch, setRequestsEpoch] = useState(0)
    const [tendersEpoch, setTendersEpoch] = useState(0)
    const [applicationsEpoch, setApplicationsEpoch] = useState(0)
    const [lettersEpoch, setLettersEpoch] = useState(0)
    // Компактні блоки «Загального» перечитуються при поверненні на вкладку:
    // контакт чи справу могли змінити на сусідніх вкладках
    const [generalEpoch, setGeneralEpoch] = useState(0)

    const fields = crudContext?.resource?.fields ?? {}
    const uuid = formContext?.values?.uuid
    const isNew = typeof uuid !== 'string' || uuid === ''
    // Обчислюване зі статусу (`SPEC-009` §5.3.4): у формі його не правлять
    const isVerified = formContext?.values?.isVerified === true

    // Поля, додані в ресурс пізніше, не мають зникати мовчки
    const rest = Object.keys(fields).filter((name) => !PLACED.has(name))

    const cell = (name: string): ReactNode => {
        if (isNew && SERVER_FIELDS.has(name)) return null

        if (name === STATUS) {
            return formContext ? (
                <FieldCell key={name}>
                    <ClientStatusCell formContext={formContext} />
                </FieldCell>
            ) : null
        }

        if (name === RESPONSIBLES) {
            return name in fields ? (
                <FieldCell key={name}>
                    <FormField name={name} />
                    <Hint id="crm.client.responsibles">
                        Менеджер бачить і змінює лише клієнтів, де він серед відповідальних; логіст бачить усіх, а
                        змінює лише таких. Обмеження не діє, якщо в людини є ще роль «Кам», «Фінанси» чи
                        «Адміністратор».
                    </Hint>
                </FieldCell>
            ) : null
        }

        const currency = COST_BY_AMOUNT.get(name)

        if (currency !== undefined) {
            return formContext && name in fields && currency in fields ? (
                <FieldCell key={name}>
                    <CostCurrencyField
                        formContext={formContext}
                        amount={name}
                        currency={currency}
                        label={CLIENT_FIELD_LABELS[name as keyof typeof CLIENT_FIELD_LABELS]}
                    />
                </FieldCell>
            ) : null
        }

        if (!(name in fields)) return null

        return (
            <FieldCell key={name} wide={WIDE.has(name)}>
                <FormField name={name} />
            </FieldCell>
        )
    }

    const grid = (names: readonly string[]) => (
        <Form formContext={formContext}>
            <FieldGrid>{names.map(cell)}</FieldGrid>
        </Form>
    )

    const main = (
        <>
            {/* Після 422 «Відправити на верифікацію» картка відкривається в
                редагуванні: перелік незаповненого — першим, бо частина полів
                лежить нижче першого екрана (`SPEC-009` §8.6, КЛ-17) */}
            {formContext && !isNew ? (
                <VerificationRequirementsAlert
                    formContext={formContext}
                    restUrl={crudContext?.resource?.restUrl ?? CLIENT_REST_URL}
                    fieldLabels={CLIENT_FIELD_LABELS}
                />
            ) : null}
            <FieldSection title="Загальне">{grid(SECTIONS.general)}</FieldSection>
            {/* Компактні «Справи» — у широкій лівій колонці: у вузькій правій
                таблиця з типом, терміном, відповідальним і статусом не вміщалася
                (замовник, 2026-10-14; `SPEC-011` §8.6 В3, В10) — лише в
                збереженого власника й тому, хто бачить справи */}
            {formContext && !isNew && canReadActivities() ? (
                <FieldSection title="Справи">
                    <CrmActivitiesTab
                        key={generalEpoch}
                        mode="compact"
                        owner="client"
                        ownerFormContext={formContext}
                    />
                </FieldSection>
            ) : null}
            <FieldSection title="Реквізити">{grid(SECTIONS.requisites)}</FieldSection>
            <FieldSection title="Верифікація">
                <Hint id="crm.client.verification">
                    Для відправки на верифікацію потрібні «Повна назва», «Код ЄДРПОУ» і всі поля від «Експедитор
                    клієнта» до «Термін оплати клієнта»; суми й термін оплати мають бути більшими за нуль.
                </Hint>
                {grid(SECTIONS.verification)}
            </FieldSection>
            <FieldSection title="Додатково" hidden={rest.length === 0}>
                {grid(rest)}
            </FieldSection>
            <FieldSection title="Службова інформація" hidden={isNew}>
                {grid(SECTIONS.service)}
            </FieldSection>
        </>
    )

    const aside = (
        <>
            {/* Компактні «Контакти» — праворуч над коментарями, списком карток
                (замовник, 2026-10-14: поміняти місцями зі «Справами»); лише в
                збереженого власника */}
            {formContext && !isNew ? (
                <FieldSection title="Контакти">
                    <CrmContactsTab
                        key={generalEpoch}
                        mode="compact"
                        resource={CLIENT_CONTACT_RESOURCE}
                        restUrl={CLIENT_CONTACT_REST_URL}
                        ownerField="client"
                        ownerFormContext={formContext}
                    />
                </FieldSection>
            ) : null}
            <CrmCommentsAside
                restUrl={CLIENT_COMMENT_REST_URL}
                ownerField="client"
                formContext={formContext}
                unsavedText="Коментувати можна буде після збереження клієнта."
            />
        </>
    )

    if (isNew || !formContext) {
        return (
            <EntityLayout>
                {formContext ? <ClientCreateDefaults formContext={formContext} /> : null}
                <EntityBody main={main} aside={aside} />
            </EntityLayout>
        )
    }

    return (
        <EntityLayout>
            <EntityTabs
                header={<ClientCardHeader formContext={formContext} />}
                onChange={(key) => {
                    if (key === 'general') setGeneralEpoch((epoch) => epoch + 1)
                    if (key === 'contacts') setContactsEpoch((epoch) => epoch + 1)
                    if (key === 'contracts') setContractsEpoch((epoch) => epoch + 1)
                    if (key === 'documents') setDocumentsEpoch((epoch) => epoch + 1)
                    if (key === 'letters') setLettersEpoch((epoch) => epoch + 1)
                    if (key === 'activities') setActivitiesEpoch((epoch) => epoch + 1)
                    if (key === 'requests') setRequestsEpoch((epoch) => epoch + 1)
                    if (key === 'tenders') setTendersEpoch((epoch) => epoch + 1)
                    if (key === 'applications') setApplicationsEpoch((epoch) => epoch + 1)
                    if (key === 'history') setHistoryEpoch((epoch) => epoch + 1)
                }}
                items={[
                    { key: 'general', label: 'Загальне', children: <EntityBody main={main} aside={aside} /> },
                    {
                        key: 'contacts',
                        label: 'Контакти',
                        children: (
                            <EntityPanel>
                                <CrmContactsTab
                                    key={contactsEpoch}
                                    resource={CLIENT_CONTACT_RESOURCE}
                                    restUrl={CLIENT_CONTACT_REST_URL}
                                    ownerField="client"
                                    ownerFormContext={formContext}
                                />
                            </EntityPanel>
                        ),
                    },
                    {
                        key: 'contracts',
                        label: 'Договори',
                        children: (
                            <EntityPanel>
                                <CrmContractsTab
                                    key={contractsEpoch}
                                    resource={CLIENT_CONTRACT_RESOURCE}
                                    restUrl={CLIENT_CONTRACT_REST_URL}
                                    ownerField="client"
                                    ownerFormContext={formContext}
                                />
                            </EntityPanel>
                        ),
                    },
                    // «Друковані форми» — лише верифікованому клієнту (КЛ-26):
                    // генерація бере дані верифікації й договору
                    ...(isVerified
                        ? [
                              {
                                  key: 'documents',
                                  label: 'Друковані форми',
                                  children: (
                                      <EntityPanel>
                                          <CrmDocumentsTab
                                              key={documentsEpoch}
                                              resource={CLIENT_DOCUMENT_RESOURCE}
                                              restUrl={CLIENT_DOCUMENT_REST_URL}
                                              ownerField="client"
                                              ownerFormContext={formContext}
                                          />
                                      </EntityPanel>
                                  ),
                              },
                          ]
                        : []),
                    // «Листи» (`SPEC-013` §9.2, S8b) — лише тому, хто бачить листи;
                    // вміст — `LetterContainer/Hooks`
                    ...(canReadLetters()
                        ? [
                              {
                                  key: 'letters',
                                  label: 'Листи',
                                  children: (
                                      <EntityPanel>
                                          <CrmLettersTab
                                              key={lettersEpoch}
                                              owner="client"
                                              ownerFormContext={formContext}
                                          />
                                      </EntityPanel>
                                  ),
                              },
                          ]
                        : []),
                    // «Справи» (`SPEC-009` §8.2, `SPEC-011` §8.6) —
                    // лише тому, хто бачить справи (В4)
                    ...(canReadActivities()
                        ? [
                              {
                                  key: 'activities',
                                  label: 'Справи',
                                  children: (
                                      <EntityPanel>
                                          <CrmActivitiesTab
                                              key={activitiesEpoch}
                                              owner="client"
                                              ownerFormContext={formContext}
                                          />
                                      </EntityPanel>
                                  ),
                              },
                          ]
                        : []),
                    // Перевезення (`SPEC-025` §3.5): «Запити», «Тендер», «Заявки» —
                    // порядок легасі (`ClientFormTemplate.jsx:150-204`); кожна — лише
                    // тому, хто бачить відповідний реєстр («Фінанси» запитів і
                    // тендерів не бачать, Q2.13)
                    ...(canReadRequests()
                        ? [
                              {
                                  key: 'requests',
                                  label: 'Запити',
                                  children: (
                                      <EntityPanel>
                                          <ClientRequestsTab key={requestsEpoch} record={formContext?.values} />
                                      </EntityPanel>
                                  ),
                              },
                          ]
                        : []),
                    ...(canReadTenders()
                        ? [
                              {
                                  key: 'tenders',
                                  label: 'Тендер',
                                  children: (
                                      <EntityPanel>
                                          <ClientTendersTab key={tendersEpoch} record={formContext?.values} />
                                      </EntityPanel>
                                  ),
                              },
                          ]
                        : []),
                    ...(canReadApplications()
                        ? [
                              {
                                  key: 'applications',
                                  label: 'Заявки',
                                  children: (
                                      <EntityPanel>
                                          <ClientApplicationsTab key={applicationsEpoch} record={formContext?.values} />
                                      </EntityPanel>
                                  ),
                              },
                          ]
                        : []),
                    {
                        key: 'history',
                        label: 'Історія змін',
                        children: (
                            <EntityPanel>
                                <CrmHistoryTab
                                    key={historyEpoch}
                                    restUrl={crudContext?.resource?.restUrl ?? CLIENT_REST_URL}
                                    uuid={uuid}
                                    events={CLIENT_HISTORY_EVENT_MAP}
                                />
                            </EntityPanel>
                        ),
                    },
                ]}
            />
        </EntityLayout>
    )
})
